/**
 * ErrorCatalog.ts
 * 
 * Centralized error handling user-facing messages.
 * Maps technical errors and status codes to friendly Spanish messages.
 */

export interface AppError {
    title: string;
    message: string;
    type: 'error' | 'warning' | 'info';
}

const ERROR_CATALOG: Record<string, AppError> = {
    // HTTP Status Codes
    '400': {
        title: 'Datos Incorrectos',
        message: 'Por favor verifica la información ingresada e intenta nuevamente.',
        type: 'warning'
    },
    '401': {
        title: 'Sesión Expirada',
        message: 'Tu sesión ha caducado. Por favor inicia sesión de nuevo.',
        type: 'warning'
    },
    '403': {
        title: 'Acceso Denegado',
        message: 'No tienes permisos para realizar esta acción.',
        type: 'error'
    },
    '404': {
        title: 'No Encontrado',
        message: 'El recurso que buscas no existe o fue eliminado.',
        type: 'warning'
    },
    '409': {
        title: 'Conflicto de Horario',
        message: 'Ya existe una cita o evento en el horario seleccionado.',
        type: 'warning'
    },
    '500': {
        title: 'Error del Servidor',
        message: 'Ocurrió un problema inesperado. Nuestro equipo ha sido notificado.',
        type: 'error'
    },
    'NETWORK_ERROR': {
        title: 'Sin Conexión',
        message: 'No pudimos conectar con el servidor. Verifica tu conexión a internet.',
        type: 'error'
    },

    // Specific Keywords (Partial matches)
    'overlap': {
        title: 'Horario Ocupado',
        message: 'El estilista ya tiene una cita en este horario.',
        type: 'warning'
    },
    'specialty': {
        title: 'Especialidad Requerida',
        message: 'El estilista seleccionado no puede realizar este servicio.',
        type: 'warning'
    },
    'subscription': {
        title: 'Suscripción Inactiva',
        message: 'La suscripción del salón no está activa. Contacta a soporte.',
        type: 'error'
    }
};

const DEFAULT_ERROR: AppError = {
    title: 'Algo salió mal',
    message: 'Ocurrió un error inesperado al procesar tu solicitud.',
    type: 'error'
};

/**
 * Transforms any error object into a user-friendly AppError
 */
export const getUserFriendlyError = (error: any): AppError => {
    // 1. Check for explicit backend message first (if it's clean)
    // Sometimes backend sends good messages, sometimes technical garbage.
    // We can filter out "Request failed" or SQL errors here.
    const rawMessage = error.response?.data?.message || error.message || '';

    // Filter out SQL or code-heavy messages
    if (rawMessage.includes('SQL') || rawMessage.includes('Exception') || rawMessage.includes('JSON')) {
        return ERROR_CATALOG['500'];
    }

    // 2. Map by Status Code
    const status = error.response?.status?.toString();
    if (status && ERROR_CATALOG[status]) {
        // If we have a specific message from backend that is "clean", use it combined with title
        // Otherwise use catalog message
        if (rawMessage && !rawMessage.includes('status code')) {
            return { ...ERROR_CATALOG[status], message: rawMessage };
        }
        return ERROR_CATALOG[status];
    }

    // 3. Map by Keyword in text
    const lowerMsg = rawMessage.toLowerCase();
    for (const key of Object.keys(ERROR_CATALOG)) {
        if (lowerMsg.includes(key)) {
            return ERROR_CATALOG[key];
        }
    }

    // 4. Fallback
    return DEFAULT_ERROR;
};
